import { useEffect, useState } from "react";
import { Save, Trash2 } from "lucide-react";
import { atualizarRealizadoCompetencia, criarRealizadoCompetencia, excluirRealizadoCompetencia, getRealizadoProjetos } from "../../api";
import { EMPRESAS_REALIZADO } from "../../constants/gestaoRealizado";
import { AvisoErro, Modal, botaoPequeno, inputStyle } from "./comum";

// Cadastro/edição de competência controlada — substitui o lançamento à mão na aba Controle
// comp. Ao salvar, o backend refaz a conciliação: solicitações "É competência? = Sim" do mesmo
// projeto e mês passam a abater o saldo.

const mes = (iso) => (iso ? iso.slice(0, 7) : "");

function Campo({ C, rotulo, obrigatorio, children, largura }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em", gridColumn: largura ? "1 / -1" : undefined }}>
      <span>{rotulo}{obrigatorio && <span style={{ color: C.danger }}> *</span>}</span>
      {children}
    </label>
  );
}

export function ModalCompetencia({ C, competencia, onClose, onSalvo }) {
  const editando = !!competencia;
  const [projetos, setProjetos] = useState([]);
  const [form, setForm] = useState(() => ({
    empresa: competencia?.empresa || "",
    projetoId: competencia?.projetoId ? String(competencia.projetoId) : "",
    competencia: mes(competencia?.competencia),
    valor: competencia ? String(competencia.valorCompetencia).replace(".", ",") : "",
    entrega: competencia?.entrega || "",
    gp: competencia?.gp || "",
    cliente: competencia?.cliente || "",
    expectativaFaturamento: mes(competencia?.expectativaFaturamento),
    observacao: competencia?.observacao || "",
  }));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  useEffect(() => {
    getRealizadoProjetos().then(setProjetos).catch((e) => setErro(e.message));
  }, []);

  const alterar = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const salvar = async () => {
    // "1.234,56" (BR) ou "1234.56"
    const valor = Number(form.valor.includes(",") ? form.valor.replace(/\./g, "").replace(",", ".") : form.valor);
    if (!form.empresa || !form.projetoId || !form.competencia || !(valor > 0)) {
      setErro("Preencha empresa, projeto, competência e um valor maior que zero.");
      return;
    }
    setSalvando(true);
    setErro("");
    const dados = {
      ...form,
      projetoId: Number(form.projetoId),
      valor,
      competencia: `${form.competencia}-01`,
      expectativaFaturamento: form.expectativaFaturamento ? `${form.expectativaFaturamento}-01` : null,
    };
    try {
      if (editando) await atualizarRealizadoCompetencia(competencia.id, dados);
      else await criarRealizadoCompetencia(dados);
      onSalvo();
    } catch (e) {
      setErro(e.message);
      setSalvando(false);
    }
  };

  const excluir = async () => {
    if (!window.confirm("Excluir esta competência? As solicitações que abatiam o saldo dela voltam a ficar sem saldo.")) return;
    setSalvando(true);
    try {
      await excluirRealizadoCompetencia(competencia.id);
      onSalvo();
    } catch (e) {
      setErro(e.message);
      setSalvando(false);
    }
  };

  const input = { ...inputStyle(C), textTransform: "none", fontWeight: 400, letterSpacing: 0 };
  return (
    <Modal
      C={C}
      largura={640}
      titulo={editando ? `Editar competência · ${competencia.projeto}` : "Nova competência"}
      onClose={onClose}
      rodape={
        <>
          {editando && competencia.origem === "PLATAFORMA" && (
            <button style={{ ...botaoPequeno(C.danger, true), marginRight: "auto" }} disabled={salvando} onClick={excluir}><Trash2 size={13} /> Excluir</button>
          )}
          <button style={botaoPequeno(C.muted, true)} onClick={onClose}>Cancelar</button>
          <button style={botaoPequeno(C.brand)} disabled={salvando} onClick={salvar}><Save size={13} /> {salvando ? "Salvando…" : "Salvar"}</button>
        </>
      }
    >
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        <Campo C={C} rotulo="Empresa" obrigatorio>
          <select value={form.empresa} onChange={alterar("empresa")} style={input}>
            <option value="">— escolha —</option>
            {EMPRESAS_REALIZADO.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        </Campo>
        <Campo C={C} rotulo="Projeto do cadastro" obrigatorio>
          <select value={form.projetoId} onChange={alterar("projetoId")} style={input}>
            <option value="">— escolha —</option>
            {projetos.map((p) => <option key={p.id} value={p.id}>{p.nome}{p.ativo ? "" : " (inativo)"}</option>)}
          </select>
        </Campo>
        <Campo C={C} rotulo="Competência (mês)" obrigatorio>
          <input type="month" value={form.competencia} onChange={alterar("competencia")} style={input} />
        </Campo>
        <Campo C={C} rotulo="Valor da competência (R$)" obrigatorio>
          <input inputMode="decimal" value={form.valor} onChange={alterar("valor")} placeholder="0,00" style={input} />
        </Campo>
        <Campo C={C} rotulo="Entrega / entregável" largura>
          <input value={form.entrega} onChange={alterar("entrega")} placeholder="Ex.: Entregável 1 - Plano de Trabalho" style={input} />
        </Campo>
        <Campo C={C} rotulo="GP">
          <input value={form.gp} onChange={alterar("gp")} style={input} />
        </Campo>
        <Campo C={C} rotulo="Cliente">
          <input value={form.cliente} onChange={alterar("cliente")} style={input} />
        </Campo>
        <Campo C={C} rotulo="Expectativa de faturamento (mês)">
          <input type="month" value={form.expectativaFaturamento} onChange={alterar("expectativaFaturamento")} style={input} />
        </Campo>
        <Campo C={C} rotulo="Observação" largura>
          <textarea value={form.observacao} onChange={alterar("observacao")} rows={2} style={{ ...input, resize: "vertical" }} />
        </Campo>
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 10 }}>
        Ao salvar, as solicitações do formulário marcadas “É competência? = Sim” do mesmo projeto e mês passam a abater o saldo automaticamente.
        {editando && competencia.origem !== "PLATAFORMA" && " Competência vinda da planilha: pode ser editada, mas não excluída."}
      </div>
    </Modal>
  );
}
